import { useState, useEffect, useRef } from 'react';
import { loadSession } from './quizSession';
import { authHeaders } from './apiAuth';
import { textesDesactives } from './texteReglage';

// Plafonds de rang des recettes composées côté front (le back reste agnostique :
// il exécute la recette, il ne connaît ni « amorce » ni « quiz thématique »).
const SEED_THEME_RANK = 3000;  // voisins de thème du mot d'atterrissage : restent apprenables
const SEED_FILL_RANK  = 2000;  // complément fréquent pour le 1er batch amorcé

async function postBatch(body) {
  const res = await fetch('/api/quiz_batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(res.status);
  return res.json();
}

// Demande le texte de fin de série pour un ensemble de mots.
//
// `echange` reste dans la signature parce que le serveur sait encore échanger
// deux mots, mais le client ne le demande plus jamais : le texte se choisit
// désormais APRÈS coup, sur ce qui a été répondu, et il n'y a alors plus rien à
// négocier.
//
// Rend null en cas d'échec, de 204, ou si la personne a demandé à ne plus voir
// les textes. La fonctionnalité peut manquer, elle ne peut pas casser le quiz.
async function demanderTexte(ids, filter, uid, lang, echange, themes = null) {
  if (textesDesactives() || !ids.length) return null;
  let vus = [];
  try { vus = JSON.parse(localStorage.getItem('textesVus') || '[]'); } catch { vus = []; }
  const res = await fetch('/api/texte_serie', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      user_id: uid || '', lang, rank_max: filter, ids,
      vus: vus.slice(-200), sans_echange: !echange,
      // Le filtre thématique voyage avec : l'échange ne doit pas faire entrer un
      // mot d'un autre champ lexical dans une séance qu'on a filtrée.
      themes: themes || undefined,
    }),
  });
  // 204 = aucun texte ne convient (corpus vide, ou tout déjà lu). Le corps
  // est alors VIDE et res.json() lèverait : `res.ok` ne suffit pas ici.
  if (!res.ok || res.status === 204) return null;
  return res.json();
}

const useRandomWords = (initialFilter = 500, userId = null, apiLang = 'fr', seedWordId = null, themes = null, landing = false) => {
  // Reprise de session (retour même onglet depuis /mot/…). Un ATTERRISSAGE (mot ou
  // thème via l'URL) prime et compose une séance fraîche ; un thème simplement
  // restauré, lui, doit reprendre la séance (donc ne PAS sauter la reprise).
  const restoredRef = useRef();
  if (restoredRef.current === undefined) {
    restoredRef.current = landing ? null : loadSession(apiLang, initialFilter);
  }
  const restored = restoredRef.current;

  const [words, setWords] = useState(restored?.words ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastIncorrectIds, setLastIncorrectIds] = useState(restored?.lastIncorrectIds ?? []);
  // Miroir lisible depuis `finirSerie`, qui n'a pas de dépendance sur l'état.
  const incorrectsRef = useRef(restored?.lastIncorrectIds ?? []);
  useEffect(() => { incorrectsRef.current = lastIncorrectIds; }, [lastIncorrectIds]);
  // LE LOT DE LA SÉRIE EN COURS, figé.
  //
  // Mesuré au navigateur : sur une série de 7 questions, l'algorithme tire
  // 6 lots — `QuizGame` en redemande un après chaque réponse (`loadNext` →
  // `fetchWords`) et n'en pose qu'une carte. Une « série » n'était donc pas dix
  // mots, mais dix questions prises dans dix lots différents.
  //
  // Deux conséquences, toutes deux constatées :
  //   · une révision de trois mots durait une question, puis dix mots tirés
  //     normalement la remplaçaient et la série passait de 3 à 10 ;
  //   · le texte de fin, choisi à chaque tirage, finissait attaché au DERNIER
  //     lot, celui que personne n'avait joué.
  //
  // On fige donc le lot pour toute la série. C'est ce qui rend vrai le modèle du
  // plan (« l'algo tire, on cherche le texte le plus proche, on échange deux
  // mots ») : sans lot stable, il n'y a rien à quoi accrocher un texte.
  //
  // Une séance REPRISE repart avec son lot : sans ça, revenir du dictionnaire au
  // milieu d'une série faisait tirer un lot neuf à la réponse suivante, et la
  // série finissait sur d'autres mots que ceux qu'elle avait commencés.
  const lotRef = useRef(restored?.words?.length ? restored.words.map((w) => w.id) : null);

  const userIdRef  = useRef(userId);
  const apiLangRef = useRef(apiLang);
  const themeRef   = useRef(themes);
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { apiLangRef.current = apiLang; }, [apiLang]);
  useEffect(() => { themeRef.current = themes; }, [themes]);

  // Filtre au montage : ne pas refetch quand `words` est déjà peuplé autrement
  // (reprise, ou amorce mot). Robuste au double-montage StrictMode (pas de flag
  // qu'on retourne, qui laisserait la 2e invocation écraser le batch).
  const mountFilterRef = useRef(initialFilter);

  // Numéro de la dernière requête émise. Deux tirages peuvent se croiser (un
  // « suivant » puis un changement de fréquence) : seul le plus récent a le
  // droit d'écrire dans `words`, sinon une réponse périmée écrase la bonne.
  const reqIdRef = useRef(0);

  const fetchWords = async (filter, limit = 10, incorrectIds = []) => {
    const reqId = ++reqIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const lang = apiLangRef.current;
      const uid  = userIdRef.current;

      // Série imposée en cours : on resert EXACTEMENT les mêmes mots.
      if (lotRef.current?.length) {
        const impose = await postBatch({
          lang, uid, limit: Math.min(12, Math.max(4, lotRef.current.length)),
          buckets: [{ include: lotRef.current }],
        });
        if (reqId !== reqIdRef.current) return;
        if (Array.isArray(impose) && impose.length) setWords(impose);
        return;
      }

      const th = themeRef.current;
      let data;
      if (th && th.length) {
        // Séance thématique : STRICTEMENT les mots du/des thème(s) DANS la bande de
        // fréquence choisie (thème ∩ top-N). Ex. « famille » + « 500 » = uniquement
        // les mots famille du top 500 → série de ≤ limit (tant pis s'il n'y en a que
        // deux, on ne complète PAS avec des mots hors bande). Ratés à revoir + dus du
        // thème (SRS présent), eux aussi bornés à la bande.
        const dueN = uid ? Math.max(1, Math.floor(limit / 3)) : 0;
        data = await postBatch({ lang, limit, uid, buckets: [
          ...(incorrectIds?.length ? [{ include: incorrectIds }] : []),
          ...(dueN ? [{ themes: th, due: true, rank_max: filter, n: dueN }] : []),
          { themes: th, rank_max: filter },
        ] });
      } else {
        // Quiz normal : chemin existant (SRS si connecté, sinon aléatoire par bande).
        let url = uid
          ? `/api/words_due/${uid}/${filter}/${lang}/${limit}`
          : `/api/random_words/${filter}/${lang}/${limit}`;
        if (incorrectIds && incorrectIds.length > 0) url += `?incorrect=${incorrectIds.join(',')}`;
        // SEUL ajout conservé dans ce fichier : le jeton de session. Il est exigé
        // par `/api/words_due` depuis le correctif de sécurité — sans lui, cette
        // route livrait la liste des mots révisés à quiconque connaissait l'UUID
        // d'un compte. Comportement inchangé pour un utilisateur légitime, et
        // repli sur le tirage anonyme si la session a expiré.
        let res = await fetch(url, { headers: authHeaders() });
        if (res.status === 403) {
          res = await fetch(`/api/random_words/${filter}/${lang}/${limit}`);
        }
        if (!res.ok) throw new Error(res.status);
        data = await res.json();
      }
      if (reqId !== reqIdRef.current) return;   // une requête plus récente a pris la main

      // PREMIER lot de la série : on cherche le texte le plus proche et on
      // échange au plus deux mots pour resserrer le lien, exactement comme le
      // plan le prévoit. L'échange n'a de sens QU'ICI, une fois par série : le
      // faire à chaque réponse injectait des mots venus d'un texte qui ne serait
      // jamais montré. Le back refuse de sacrifier un mot dû.
      const r = await demanderTexte(data.map((w) => w.id), filter, uid, lang, true, th);
      if (reqId !== reqIdRef.current) return;
      if (r?.sortants?.length) {
        const sortants = new Set(r.sortants);
        const entrants = [...(r.entrants || [])];
        data = data.map((w) => (sortants.has(w.id) && entrants.length ? entrants.shift() : w));
      }
      // Le texte n'est PAS retenu ici : il sera redemandé en fin de série sur les
      // mots réellement répondus. L'échange sert à faire en sorte qu'ils soient
      // dans le texte ; c'est à l'arrivée qu'on constate.
      lotRef.current = data.map((w) => w.id);
      setWords(data);
    } catch (err) {
      if (reqId === reqIdRef.current) setError(err.message);
    } finally {
      if (reqId === reqIdRef.current) setIsLoading(false);
    }
  };

  // Amorce mot (/?mot=<id>) : 1er batch = le mot + des mots de son thème, puis
  // complément fréquent. Les batches suivants repassent par fetchWords (thème
  // inactif → algo de base) : l'amorce est transitoire.
  useEffect(() => {
    if (!seedWordId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await postBatch({
          lang: apiLangRef.current, limit: 10, uid: userIdRef.current,
          buckets: [
            { include: [seedWordId] },
            { like_themes_of: seedWordId, rank_max: SEED_THEME_RANK, n: 5 },
            { rank_max: SEED_FILL_RANK },
          ],
        });
        if (cancelled) return;
        if (Array.isArray(data) && data.length) setWords(data);
        else fetchWords(initialFilter);        // mot inconnu → tirage normal
      } catch {
        if (!cancelled) fetchWords(initialFilter);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Montage : tirage normal SAUF reprise/amorce-mot (peuplés autrement). Un thème
  // d'atterrissage passe par ce chemin (fetchWords → recette thème via themeRef).
  //
  // C'est aussi le SEUL point de tirage lors d'un changement de fréquence : App
  // se contente de poser `frequency`. Quand App appelait en plus fetchWords, cet
  // effet en lançait un second, et les deux `ORDER BY RANDOM()` se disputaient
  // l'écriture de `words` (question qui change sous les doigts).
  useEffect(() => {
    if (initialFilter === mountFilterRef.current && (restored || seedWordId)) return;
    mountFilterRef.current = initialFilter;
    lotRef.current = null;   // changer de bande sort d'une série imposée
    fetchWords(initialFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  // Entrée/sortie/changement de thème(s) APRÈS le montage → recomposer la séance.
  // Robuste au double-montage : on compare une CLÉ stable (slugs joints), pas la
  // référence du tableau (recréé à chaque rendu côté App).
  const lastThemeKeyRef = useRef(themes ? themes.join(',') : '');
  useEffect(() => {
    const key = themes ? themes.join(',') : '';
    if (key === lastThemeKeyRef.current) return;
    lastThemeKeyRef.current = key;
    setLastIncorrectIds([]);
    lotRef.current = null;   // changer de thème aussi
    fetchWords(initialFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes]);

  // Série IMPOSÉE : les mots viennent d'une liste choisie dans le panneau de
  // compte (« à revoir », « en erreur », « appris »), pas du tirage.
  //
  // On envoie la liste ENTIÈRE, pas les dix premiers : quand elle est plus
  // longue qu'une série, c'est le back qui choisit les dix, en prenant ceux qui
  // composent le meilleur texte. Envoyer une tranche d'avance lui retirerait
  // précisément le choix qu'on lui demande de faire.
  //
  // On ne complète JAMAIS une liste trop courte : deux erreurs à revoir font
  // une série de deux. Et le texte n'est pas forcé : s'il n'y en a pas, la
  // série se déroule sans.
  //
  // La série suivante repasse par `fetchWords` et redevient normale :
  // l'imposition ne dure qu'une série, comme l'amorce d'un mot d'atterrissage.
  const lancerSerie = async (ids) => {
    if (!Array.isArray(ids) || !ids.length) return;
    const reqId = ++reqIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const lang = apiLangRef.current;
      const uid  = userIdRef.current;
      let choisis = ids.slice(0, 12);
      if (!textesDesactives()) {
        let vus = [];
        try { vus = JSON.parse(localStorage.getItem('textesVus') || '[]'); } catch { vus = []; }
        try {
          const res = await fetch('/api/serie_mots', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
              user_id: uid || '', lang, rank_max: initialFilter,
              ids, limit: 10, vus: vus.slice(-200),
            }),
          });
          if (res.ok && res.status !== 204) {
            const r = await res.json();
            // On ne garde QUE le choix des mots. Le texte que le serveur a
            // trouvé au passage n'est pas affiché : celui de la fin de série est
            // redemandé sur les mots RÉELLEMENT répondus.
            if (Array.isArray(r.ids) && r.ids.length) choisis = r.ids;
          }
        } catch { /* pas de texte : on révise quand même la liste demandée */ }
      }
      if (reqId !== reqIdRef.current) return;
      // `include` : la même recette que la CTA /?mot= des fiches. Rien de neuf
      // côté back, et le SRS enregistre chaque réponse comme d'habitude.
      const data = await postBatch({
        lang, uid, limit: Math.min(12, Math.max(4, choisis.length)),
        buckets: [{ include: choisis }],
      });
      if (reqId !== reqIdRef.current) return;
      if (!Array.isArray(data) || !data.length) return;
      lotRef.current = choisis;
      setLastIncorrectIds([]);
      setWords(data);
    } catch (err) {
      if (reqId === reqIdRef.current) setError(err.message);
    } finally {
      if (reqId === reqIdRef.current) setIsLoading(false);
    }
  };

  // Fin de la série imposée : on rend la main au tirage normal, et on va
  // chercher tout de suite un lot ordinaire. Sans ce tirage, la première carte
  // de la série SUIVANTE serait encore prise dans les mots imposés.
  // Rend une promesse : App attend le nouveau lot AVANT de rendre la main au
  // quiz, sinon celui-ci réapparaît une fraction de seconde sur la carte de la
  // série précédente.
  const finirSerie = () => {
    if (!lotRef.current) return Promise.resolve();
    lotRef.current = null;
    // Les mots ratés partent AVEC : le lot étant figé pendant la série, un mot
    // manqué ne peut plus y revenir (c'est le prix de « chaque mot posé une
    // fois »). Sans ce report, il ne serait jamais repris, alors que c'est
    // exactement celui qu'il faut revoir.
    return fetchWords(initialFilter, 10, incorrectsRef.current);
  };

  // Le texte de fin de série, demandé À LA FIN, pour les mots que la personne a
  // RÉELLEMENT répondus.
  //
  // Il était choisi à chaque tirage de lot, et c'était faux : `QuizGame` retire
  // un lot frais après chaque réponse, donc le dernier lot tiré — celui pour
  // lequel le texte était choisi — contenait dix mots que la personne n'avait
  // pas vus. On surlignait des mots jamais travaillés (« je n'avais pas тут
  // dans la série pourtant »). Mesuré : sur une séance de sept questions, deux
  // des sept mots surlignés n'avaient jamais été posés.
  //
  // L'échange de deux mots disparaît avec ça, et c'est cohérent : il servait à
  // rapprocher le lot d'un texte choisi pour LUI. Un texte choisi après coup n'a
  // rien à négocier, il constate. C'était d'ailleurs le repli prévu au plan
  // (« si l'inversion déçoit, on retire le include et il ne reste rien »).
  const demanderTexteFinal = async (ids) => {
    try {
      const r = await demanderTexte(ids, initialFilter, userIdRef.current,
                                    apiLangRef.current, false);
      return r?.texte || null;
    } catch {
      return null;   // pas de texte : le bilan s'affiche seul, comme avant
    }
  };

  return { words, isLoading, error, fetchWords, lancerSerie, finirSerie,
           demanderTexteFinal, lastIncorrectIds, setLastIncorrectIds };
};

export default useRandomWords;
